import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Services } from '../../assets';
import gsap from 'gsap';

const Banner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handRef = useRef<HTMLImageElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const subheadingRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleEnrollClick = () => {
    const formSection = document.getElementById('landing-page-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Bouncing hand animation
    if (handRef.current) {
      gsap.to(handRef.current, {
        y: -20,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
        duration: 0.8,
      });
    }

    // Fade down animations
    gsap.from(headingRef.current, {
      opacity: 1,
      y: -30,
      duration: 1,
      ease: 'power2.out',
    });

    gsap.from(subheadingRef.current, {
      opacity: 1,
      y: -30,
      delay: 0.3,
      duration: 1,
      ease: 'power2.out',
    });

    gsap.from(contentRef.current, {
      opacity: 1,
      y: -30,
      delay: 0.6,
      duration: 1,
      ease: 'power2.out',
    });
  }, []);

  return (
    <Box
      sx={{
        backgroundImage: isMobile
          ? 'url("https://dprstorage.b-cdn.net/RW/Landing%20mobilebanner.png")'
          : 'url("https://dprstorage.b-cdn.net/RW/landingpagebanner.png")',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom',
        minHeight: '100vh',
        color: '#000',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {/* Headings */}
      <Box ref={headingRef}>
        <Typography
          variant='h2'
          textAlign="center"
          color="#0056B3"
          mb={1}
          sx={{
            fontSize: { xs: '30px', md: '54px', lg: '54px' },
            fontWeight: 800,
            mt: 16
          }}
        >
          NO FLUFF. NO THEORY.
        </Typography>
      </Box>

      <Box ref={subheadingRef}>
        <Typography
          className='akirafont'
          variant={isMobile ? 'h5' : 'h3'}
          textAlign="center"
          fontWeight="bold"
          sx={{
            mb: 6,
            letterSpacing: 1,
            fontSize: { xs: '27px', md: '54px', lg: '54px' },
            mt:3
          }}
        >
          ONLY ACTIONABLE STRATEGIES
        </Typography>
      </Box>

      {/* Content Section */}
      <Box ref={contentRef}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row-reverse',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: 4,
            px: isMobile ? 2 : 8,
            mt: 4,
          }}
        >
          {/* Right block */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
            width="100%"
          >
            <Box textAlign="right">
              <Typography variant="subtitle2" fontStyle="italic" fontWeight={600}>
                Learn to Sell Like <br />
                the Pro's
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: '#0056B3', width: '3px' }} />
          </Box>

          {/* Left block - desktop */}
          {!isMobile && (
            <Box display="flex" alignItems="center" justifyContent="start" gap={2}>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: '#0056B3', width: '3px' }} />
              <Box>
                <Typography variant="body1" sx={{ textAlign: 'start' }}>
                  Unlock Exclusive Insights in a
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ textAlign: 'start', width: 350 }}>
                  4/6-HOUR POWERPACKED <br />WORKSHOPS
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Mobile version of left block */}
        {isMobile && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="start"
            gap={2}
            mt={4}
            px={2}
          >
            <Divider orientation="vertical" flexItem sx={{ bgcolor: '#0056B3', width: '3px' }} />
            <Box>
              <Typography variant="body1" sx={{ textAlign: 'start' }}>
                Unlock Exclusive Insights in a
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ textAlign: 'start' }}>
                4/6-HOUR POWERPACKED <br />WORKSHOPS
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* CTA Button + Hand Image */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'center' : 'flex-end',
          justifyContent: 'flex-end',
          gap: 2,
          zIndex: 10,
          px: 8,
          mt: { xs: 8, md: 4, lg: 4 },
        }}
      >
        <Button
          onClick={handleEnrollClick}
          sx={{
            fontSize: { xs: '14px', md: '18px', lg: '24px' },
            px: 5,
            py: 2.3,
            fontWeight: 'bold',
            borderRadius: '12px',
            background: 'linear-gradient(to top, rgba(141,171,201,0.5) 20%, rgba(255,255,255,1) 50%)',
            color: '#000',
            border: '2px solid #0f63a5',
            '&:hover': {
              backgroundColor: '#0056B3',
              color: '#fff',
              borderColor: '#0056B3',
            },
          }}
        >
          ENROLL NOW!
        </Button>

        <Box
          ref={handRef}
          component="img"
          src={Services.handpointup}
          alt="Rotated Icon"
          sx={{
            width: 60,
            height: 60,
            transform: 'rotate(330deg)',
            position: 'relative',
            bottom: 20,
            right: -26,
          }}
        />
      </Box>
    </Box>
  );
};

export default Banner;
